import React, { Component, createRef } from 'react';
import { connect } from 'react-redux';
import { withRouter, Link } from 'react-router-dom';
import cx from 'classnames';

import LinksGroup from './LinksGroup/LinksGroup';
import { fetchReportedIssuesCount } from '../../actions/driverIssues';
import { fetchEscalationCount, updateIdleDriverCount } from '../../actions/escalationCount';
import { fetchCustomerEscalationCount } from '../../actions/customerEscalation';
import { fetchUnassignedBookingsCount, updateUnassignedCount } from '../../actions/unassignedBookings';
import { fetchPendingDriverRegistrationCount } from '../../actions/pendingDriverRegistration';
import opsWebSocket from '../../services/opsWebSocket';
import { showSupportToast, showKnowledgeGapToast } from '../../utils/supportToast';
import config from '../../config';
import { fetchCallManagerQueueSnapshot } from '../../utils/marketingApi';

import s from './Sidebar.module.scss';

const SIDEBAR_POLL_MS = 60000;

class Sidebar extends Component {
  constructor(props) {
    super(props);
    this.navRef = createRef();
    this.state = {
      pendingSupportCount: 0,
      dueLeadsCount: 0,
      newVehicleLeadsCount: 0,
      callManagerActiveBatches: 0,
      knowledgeGapCount: 0,
      isSidebarScrollable: false,
      notificationDismissed: false,
    };
  }

  checkSidebarScrollable = () => {
    const el = this.navRef.current;
    if (el) {
      const isScrollable = el.scrollHeight > el.clientHeight;
      if (isScrollable !== this.state.isSidebarScrollable) {
        this.setState({ isSidebarScrollable: isScrollable });
      }
    }
  };

  dismissKnowledgeNotification = (e) => {
    e.stopPropagation();
    this.setState({ notificationDismissed: true });
  };

  resetNotificationDismissed = () => {
    this.setState({ notificationDismissed: false });
  };

  fetchPendingSupportCount = async () => {
    try {
      const res = await fetch(`${config.customerApiBase}/support-tickets/pending-count`);
      if (!res.ok) return;
      const data = await res.json();
      this.setState({ pendingSupportCount: data.count || 0 });
    } catch (err) {
      console.error('Failed to fetch pending support count', err);
    }
  };

  fetchCallManagerActiveBatches = async () => {
    try {
      const data = await fetchCallManagerQueueSnapshot();
      this.setState({ callManagerActiveBatches: data?.active_batches || 0 });
    } catch (err) {
      console.error('Failed to fetch call manager active batches', err);
    }
  };

  fetchDueLeadsCount = async () => {
    try {
      const res = await fetch(`${config.customerApiBase}/booking-leads/count?group=true`);
      if (!res.ok) return;
      const data = await res.json();
      this.setState({ dueLeadsCount: data.counts?.new ?? 0 });
    } catch (err) {
      console.error('Failed to fetch leads count', err);
    }
  };

  fetchNewVehicleLeadsCount = async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('id_token') || '';
      const res = await fetch(`${config.opsApiBase}/vehicle-leads/counts`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) return;
      const data = await res.json();
      this.setState({ newVehicleLeadsCount: data.counts?.new ?? 0 });
    } catch (err) {
      console.error('Failed to fetch vehicle leads count', err);
    }
  };

  fetchKnowledgeGapCount = async () => {
    try {
      const res = await fetch(`${config.customerApiUrl}/knowledge-gaps/count`);
      if (!res.ok) return;
      const data = await res.json();
      this.setState({ knowledgeGapCount: data.count || 0 });
    } catch (err) {
      console.error('Failed to fetch knowledge gap count', err);
    }
  };

  componentDidMount() {
    // Track if user was on knowledge page (instance variable, not state)
    this.wasOnKnowledgePage = this.props.location.pathname.startsWith('/app/knowledge/');

    this.props.dispatch(fetchReportedIssuesCount());
    this.props.dispatch(fetchUnassignedBookingsCount());
    this.props.dispatch(fetchEscalationCount());
    this.props.dispatch(fetchCustomerEscalationCount());
    this.fetchPendingSupportCount();
    this.fetchDueLeadsCount();
    this.fetchNewVehicleLeadsCount();
    this.fetchCallManagerActiveBatches();
    this.fetchKnowledgeGapCount();
    this.props.dispatch(fetchPendingDriverRegistrationCount());

    // Check if sidebar is scrollable after initial render
    setTimeout(this.checkSidebarScrollable, 100);
    window.addEventListener('resize', this.checkSidebarScrollable);

    this.countInterval = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      this.props.dispatch(fetchReportedIssuesCount());
      this.props.dispatch(fetchUnassignedBookingsCount());
      this.props.dispatch(fetchEscalationCount());
      this.props.dispatch(fetchCustomerEscalationCount());
      this.fetchPendingSupportCount();
      this.fetchDueLeadsCount();
      this.fetchNewVehicleLeadsCount();
      this.fetchCallManagerActiveBatches();
      this.fetchKnowledgeGapCount();
      this.props.dispatch(fetchPendingDriverRegistrationCount());
    }, SIDEBAR_POLL_MS);


    this.unsubEscalation = opsWebSocket.subscribe('escalation', (data) => {
      if (typeof data.count === 'number') {
        this.props.dispatch(updateUnassignedCount(data.count));
      } else if (Array.isArray(data.unassigned)) {
        this.props.dispatch(updateUnassignedCount(data.unassigned.length));
      }
    });
    this.unsubIdleDrivers = opsWebSocket.subscribe('idle_drivers', (data) => {
      let drivers = Array.isArray(data?.drivers) ? data.drivers : [];
      // Filter out dismissed idle drivers (same logic as TripEscalations)
      try {
        const stored = localStorage.getItem('dismissedIdleKeys');
        if (stored) {
          const dismissed = new Set(JSON.parse(stored));
          drivers = drivers.filter(d => {
            const key = `${d.driver_id}:${d.last_trip_id || ''}`;
            return !dismissed.has(key);
          });
        }
      } catch {}
      this.props.dispatch(updateIdleDriverCount(drivers.length));
    });
    this.unsubClear = opsWebSocket.subscribe('clear_all', () => {
      this.props.dispatch(updateUnassignedCount(0));
      this.props.dispatch(updateIdleDriverCount(0));
    });

    this.unsubSupportNew = opsWebSocket.subscribe('support_ticket_created', (data) => {
      this.fetchPendingSupportCount();
      if (data?.ticket_id) {
        showSupportToast(
          <div className="custom-toast-content support-ticket-toast">
            <div className="toast-icon">
              <i className="fa fa-ticket" />
            </div>
            <div className="toast-body">
              <div className="toast-title">New Support Ticket</div>
              <div className="toast-message">
                <strong>TKT-{data.ticket_id}</strong>: {data.subject}
                <br />
                <strong>Client</strong>: {data.org_name} ({data.created_by_name})
              </div>
            </div>
          </div>,
        );
      }
    });
    this.unsubSupportRequest = opsWebSocket.subscribe('support_connect_request', (data) => {
      this.fetchPendingSupportCount();
      if (data?.is_live_request) {
        showSupportToast(
          <div className="custom-toast-content support-ticket-toast">
            <div className="toast-icon">
              <i className="fa fa-phone" />
            </div>
            <div className="toast-body">
              <div className="toast-title">Live Support Requested</div>
              <div className="toast-message">
                <strong>TKT-{data.ticket_id}</strong>: {data.subject}
                <br />
                <strong>Client</strong>: {data.org_name} ({data.created_by_name})
              </div>
            </div>
          </div>,
        );
      }
    });
    this.unsubSupportAccept = opsWebSocket.subscribe('support_connect_accept', () => {
      this.fetchPendingSupportCount();
    });
    this.unsubSupportCancel = opsWebSocket.subscribe('support_connect_cancel', () => {
      this.fetchPendingSupportCount();
    });
    this.unsubSupportClose = opsWebSocket.subscribe('support_connect_close', () => {
      this.fetchPendingSupportCount();
    });
  }

  componentWillUnmount() {
    if (this.countInterval) {
      clearInterval(this.countInterval);
    }
    if (this.unsubEscalation) this.unsubEscalation();
    if (this.unsubIdleDrivers) this.unsubIdleDrivers();
    if (this.unsubClear) this.unsubClear();
    if (this.unsubSupportNew) this.unsubSupportNew();
    if (this.unsubSupportRequest) this.unsubSupportRequest();
    if (this.unsubSupportAccept) this.unsubSupportAccept();
    if (this.unsubSupportCancel) this.unsubSupportCancel();
    if (this.unsubSupportClose) this.unsubSupportClose();
    window.removeEventListener('resize', this.checkSidebarScrollable);
  }

  componentDidUpdate(prevProps, prevState) {
    const currentPath = this.props.location.pathname;
    const isOnKnowledgePage = currentPath.startsWith('/app/knowledge/');

    // User navigated FROM knowledge page TO elsewhere → re-enable notification
    if (this.wasOnKnowledgePage && !isOnKnowledgePage) {
      this.setState({ notificationDismissed: false });
    }

    // Gap count increased → re-enable notification + show toast
    if (prevState.knowledgeGapCount !== this.state.knowledgeGapCount && this.state.knowledgeGapCount > prevState.knowledgeGapCount) {
      this.setState({ notificationDismissed: false });
      const newCount = this.state.knowledgeGapCount - prevState.knowledgeGapCount;
      showKnowledgeGapToast(newCount, this.props.history);
    }

    // Track knowledge page state for next comparison
    this.wasOnKnowledgePage = isOnKnowledgePage;

    this.checkSidebarScrollable();
  }

  render() {
    const { reportedCount, unassignedCount, escalationCount, customerEscalationCount, pendingDriverRegCount, sidebarOpen } = this.props;
    const { knowledgeGapCount, isSidebarScrollable, notificationDismissed } = this.state;
    const currentPath = this.props.location.pathname;
    const registerView = new URLSearchParams(this.props.location.search || '').get('registerPage');
    const logoTo =
      currentPath.startsWith('/app/dispatch/vehicles') &&
      (registerView === 'pending' || registerView === 'registered')
        ? '/app/dispatch/vehicles?tab=register-vehicle'
        : '/app/operations-dashboard';
    const isOnKnowledgePage = currentPath.startsWith('/app/knowledge/');
    const showKnowledgeNotification = knowledgeGapCount > 0 && !isOnKnowledgePage && isSidebarScrollable && !notificationDismissed;

    return (
      <nav className={cx(s.root, { [s.sidebarOpen]: sidebarOpen })}>
        <header className={s.logo}>
          <div className={s.logoWrapper}>
            <Link to={logoTo}>
              <img src={`${process.env.PUBLIC_URL || ''}/logo.png`} alt="Logo" />
            </Link>
          </div>
        </header>
        <ul className={s.nav} ref={this.navRef}>
          <LinksGroup
            header="Dashboard"
            headerLink="/app/operations-dashboard"
          />
          {/* <LinksGroup
            header="Drivers"
            headerLink="/app/drivers"
            glyph="settings"
          /> */}
          <LinksGroup
            header="Vehicles"
            headerLink="/app/dispatch/vehicles"
            badge={pendingDriverRegCount || undefined}
          />
          <LinksGroup
            header="Vehicle Leads"
            headerLink="/app/vehicle-leads"
            glyph="users"
            badge={this.state.newVehicleLeadsCount || undefined}
          />
          <LinksGroup
            header="Customers"
            headerLink="/app/customers"
            glyph="users"
          />
          <LinksGroup
            header="Leads"
            headerLink="/app/leads"
            glyph="inbox"
            badge={this.state.dueLeadsCount || undefined}
          />
          <LinksGroup
            header="Call Manager"
            headerLink="/app/call-manager"
            glyph="phone"
            badge={this.state.callManagerActiveBatches || undefined}
          />
          <LinksGroup
            header="Bookings"
            headerLink="/app/bookings"
            glyph="list"
          />

          <LinksGroup
            header="Trips"
            headerLink="/app/trips"
          />
          <LinksGroup
            header="Payment Center"
            headerLink="/app/payment-center"
          />
          <li className={s.operationsListShell}>
            <ul className={s.sidebarOperations}>
              <li className={s.sidebarSection}>
                <span>ESCALATION</span>
              </li>
              <LinksGroup
                header="Trip Escalations"
                headerLink="/app/trips/escalations"
                glyph="attention"
                badge={escalationCount}
              />
              <LinksGroup
                header="Unassigned Bookings"
                headerLink="/app/operations/escalation"
                glyph="attention"
                badge={unassignedCount}
              />
              <LinksGroup
                header="Customer escalation"
                headerLink="/app/conversations"
                glyph="user"
                badge={customerEscalationCount}
              />
              <LinksGroup
                header="Driver escalation"
                headerLink="/app/notifications"
                badge={reportedCount}
              />
              <LinksGroup
                header="Support Tickets"
                headerLink="/app/support"
                badge={this.state.pendingSupportCount || undefined}
              />
            </ul>
          </li>
          <li className={s.sidebarSection}>
            <span>LIVE TRACKING</span>
          </li>
          <LinksGroup
            header="Ops Map"
            headerLink="/app/ops/map"
            opsMap
          />
          <LinksGroup
            header="OPS Register"
            headerLink="/app/ops/register"
          />
          <li className={s.sidebarSection}>
            <span>KNOWLEDGE BASE</span>
          </li>
          <LinksGroup
            header="Knowledge Gaps"
            headerLink="/app/knowledge/gaps"
            badge={this.state.knowledgeGapCount || undefined}
          />
          <LinksGroup
            header="Documents"
            headerLink="/app/knowledge/documents"
          />
        </ul>

        {/* Discord-style floating pill notification */}
        {showKnowledgeNotification && (
          <div className={s.knowledgePill} onClick={() => this.props.history.push('/app/knowledge/gaps')}>
            <div className={s.knowledgePillIcon}>
              <i className="fa fa-exclamation" />
            </div>
            <span className={s.knowledgePillText}>{knowledgeGapCount} new gap{knowledgeGapCount !== 1 ? 's' : ''}</span>
            <button className={s.knowledgePillClose} onClick={this.dismissKnowledgeNotification}>
              <i className="fa fa-times" />
            </button>
          </div>
        )}
      </nav>
    );
  }
}

function mapStateToProps(store) {
  return {
    sidebarOpened: store.navigation.sidebarOpened,
    sidebarStatic: store.navigation.sidebarStatic,
    unassignedCount: store.unassignedBookings.unassignedCount,
    escalationCount: store.escalationCount.escalationCount || 0,
    reportedCount: store.driverIssues ? store.driverIssues.reportedCount : 0,
    customerEscalationCount: store.customerEscalation ? store.customerEscalation.customerEscalationCount : 0,
    pendingDriverRegCount: store.pendingDriverRegistration
      ? (store.pendingDriverRegistration.pendingCount || 0) + (store.pendingDriverRegistration.registeredCount || 0)
      : 0,
  };
}

export default withRouter(connect(mapStateToProps)(Sidebar));
